// GtClientRecord — the client record, in the same layout as the candidate
// record: a profile header with quick facts, actions and a stat strip, then
// tabs on the left (Activity is the source of truth) and client information,
// commercial and contacts on the right. Everything inside .gtr2 is 12px.
// Roles open in place (back returns to the client, not the roles list).
import { gtConfirm } from '../../components/GtConfirm';
import { useState, useEffect, useCallback } from 'react';
import { SectorField } from '../../components/GtTaxonomyFields';
import GtLoader from '../../components/GtLoader';
import API_BASE from '../../config';
import GtIntelligence from './GtIntelligence';
import { GtSelect } from '../../components/GtControls';
import GtRoleRecord from './GtRoleRecord';
import GtShareCreate from './GtShareCreate';
import { Ico, ICONS, Stat } from '../../components/GtRecordKit';
import GtClientFacts from '../../components/GtClientFacts'; // C1-CLIENTFACTS

function initials(name) { return String(name || '?').trim().split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'; }
const AVC = ['#1E5A7B','#2E7D32','#6A1B9A','#B8860B','#00695C','#C2410C','#374151','#9D174D','#1565C0','#4E342E','#5B6C1E','#0F766E','#B91C1C','#5D4037','#3949AB','#00838F'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return AVC[h % AVC.length]; }
function ukDate(v) { if (!v) return '—'; const d = new Date(v); return isNaN(d) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
function ukDateTime(v) { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
function relTime(v) {
  if (!v) return '';
  const d = new Date(v); if (isNaN(d)) return '';
  const s = Math.round((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.round(s / 60); if (m < 60) return m + 'm ago';
  const h = Math.round(m / 60); if (h < 24) return h + 'h ago';
  const dd = Math.round(h / 24); if (dd < 30) return dd + 'd ago';
  const mo = Math.round(dd / 30); if (mo < 12) return mo + 'mo ago';
  return Math.round(mo / 12) + 'y ago';
}
function money(n) {
  if (n === null || n === undefined || n === '') return '£0';
  const v = Number(n); if (isNaN(v)) return '£0';
  return '£' + v.toLocaleString('en-GB', { maximumFractionDigits: 0 });
}

const CLIENT_STATUS_PILL = { prospect: 'gtt-p-slate', active: 'gtt-p-green', dormant: 'gtt-p-amber', archived: 'gtt-p-slate' };
const STATUS_OPTS = [{ value: 'prospect', label: 'Prospect' }, { value: 'active', label: 'Active' }, { value: 'dormant', label: 'Dormant' }, { value: 'archived', label: 'Archived' }];
const ROLE_STATUS_PILL = { draft: 'gtt-p-slate', open: 'gtt-p-green', paused: 'gtt-p-amber', filled: 'gtt-p-blue', closed: 'gtt-p-slate' };
const SHARE_STATUS_PILL = { active: 'gtt-p-green', revoked: 'gtt-p-rose', expired: 'gtt-p-slate' };
const TABS = ['Activity', 'Overview', 'Roles', 'Shares', 'Contacts', 'Notes', 'Intelligence'];

// Per-kind presentation for the enterprise activity feed.
const ACT_KIND = {
  role:       { label: 'Role',       cls: 'gta-k-role',   icon: 'M4 7h16M4 12h16M4 17h10' },
  submission: { label: 'Submission', cls: 'gta-k-sub',    icon: 'M12 4v12m0 0l-4-4m4 4l4-4M5 20h14' },
  outcome:    { label: 'Outcome',    cls: 'gta-k-out',    icon: 'M5 12l4 4L19 6' },
  share:      { label: 'Share',      cls: 'gta-k-share',  icon: 'M8 12a3 3 0 100-2 3 3 0 000 2zm8-6a3 3 0 100 2 3 3 0 000-2zm0 12a3 3 0 100-2 3 3 0 000 2zM10.5 10.5l3-2m-3 5l3 2' },
  share_open: { label: 'Open',       cls: 'gta-k-open',   icon: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z M12 15a3 3 0 100-6 3 3 0 000 6z' },
  note:       { label: 'Note',       cls: 'gta-k-note',   icon: 'M6 4h9l5 5v11H6zM15 4v5h5' },
  contact:    { label: 'Contact',    cls: 'gta-k-contact',icon: 'M12 12a4 4 0 100-8 4 4 0 000 8zm-7 8a7 7 0 0114 0' },
  audit:      { label: 'System',     cls: 'gta-k-audit',  icon: 'M12 8v4l3 2M12 3a9 9 0 100 18 9 9 0 000-18z' },
  signal:     { label: 'Signal',     cls: 'gta-k-signal', icon: 'M12 12a2 2 0 100-4 2 2 0 000 4zM8.5 6.5a6 6 0 000 7M15.5 6.5a6 6 0 010 7M5.5 3.5a10 10 0 000 13M18.5 3.5a10 10 0 010 13' },
  candidate:  { label: 'Candidate',  cls: 'gta-k-sub',    icon: 'M12 12a4 4 0 100-8 4 4 0 000 8zm-7 8a7 7 0 0114 0' },
  compliance: { label: 'Compliance', cls: 'gta-k-audit',  icon: 'M12 3l7 3v6c0 4-3 7-7 9-4-2-7-5-7-9V6z' },
  ai:         { label: 'AI',         cls: 'gta-k-signal', icon: 'M12 3v3m0 12v3m9-9h-3M6 12H3m3.6-6.4l2.1 2.1m6.6 6.6l2.1 2.1m0-10.8l-2.1 2.1m-6.6 6.6l-2.1 2.1' },
  client:     { label: 'Client',     cls: 'gta-k-contact',icon: 'M4 20V8l6-4 6 4v12M9 20v-5h4v5' },
  pipeline:   { label: 'Pipeline',   cls: 'gta-k-sub',    icon: 'M4 6h16M7 12h10M10 18h4' },
  delivery:   { label: 'Delivery',   cls: 'gta-k-share',  icon: 'M8 12a3 3 0 100-2 3 3 0 000 2zm8-6a3 3 0 100 2 3 3 0 000-2zm0 12a3 3 0 100-2 3 3 0 000 2zM10.5 10.5l3-2m-3 5l3 2' },
  clientact:  { label: 'Client read',cls: 'gta-k-open',   icon: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z M12 15a3 3 0 100-6 3 3 0 000 6z' },
  win:        { label: 'Win',        cls: 'gta-k-out',    icon: 'M5 12l4 4L19 6' },
  loss:       { label: 'Loss',       cls: 'gta-k-out',    icon: 'M6 6l12 12M18 6L6 18' },
  intel:      { label: 'Intel',      cls: 'gta-k-signal', icon: 'M12 12a2 2 0 100-4 2 2 0 000 4zM8.5 6.5a6 6 0 000 7M15.5 6.5a6 6 0 010 7' },
  other:      { label: 'Other',      cls: 'gta-k-audit',  icon: 'M12 8v4l3 2M12 3a9 9 0 100 18 9 9 0 000-18z' },
};

export default function GtClientRecord({ clientId, getToken, userName, userImage, onBack, initialRoleId, onOpenRole, onCloseRole, initialCandidateId, onOpenCandidate, onCloseCandidate }) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('Activity');
  const [toast, setToast] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  // The open nested role is the URL (via initialRoleId from the table), not local
  // state, so a nested role deep-links and survives refresh. onOpenRole/onCloseRole
  // navigate; there is no second source of truth to drift.
  const openRoleId = initialRoleId || null;
  const setOpenRoleId = (rid) => rid ? onOpenRole && onOpenRole(rid) : onCloseRole && onCloseRole();
  const [showAddContact, setShowAddContact] = useState(false);
  const [cf, setCf] = useState({ full_name: '', job_title: '', email: '', phone: '', linkedin_url: '', is_primary: false });
  const [liBusy, setLiBusy] = useState({});

  // Notes
  const [notes, setNotes] = useState([]);
  const [notesHasMore, setNotesHasMore] = useState(false);
  const [notesCursor, setNotesCursor] = useState(null);
  const [notesLoadingMore, setNotesLoadingMore] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [myClerkId, setMyClerkId] = useState(null);

  // Commercial
  const [commercial, setCommercial] = useState(null);
  const [intel, setIntel] = useState(null);

  // Activity
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [blurbBusy, setBlurbBusy] = useState(false);
  const [blurbEditing, setBlurbEditing] = useState(false);
  const [blurbDraft, setBlurbDraft] = useState('');
  const [actFilter, setActFilter] = useState('all');

  // Shares
  const [shares, setShares] = useState(null);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [openShareId, setOpenShareId] = useState(null);
  const [shareDetail, setShareDetail] = useState(null);
  const [shareDetailLoading, setShareDetailLoading] = useState(false);
  const [showShareCreate, setShowShareCreate] = useState(false);
  const [editContactId, setEditContactId] = useState(null);
  const [ecf, setEcf] = useState({ full_name: '', job_title: '', email: '', phone: '', linkedin_url: '' });

  const authFetch = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const res = await fetch(API_BASE + path, { ...opts, headers: { ...(opts.headers || {}), Authorization: 'Bearer ' + token } });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.message || body.detail || body.error || ('request failed: ' + res.status));
    return body;
  }, [getToken]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setClient(await authFetch('/api/clients/' + clientId));
      const np = await authFetch('/api/clients/' + clientId + '/notes?limit=20');
      setNotes(np.notes || []); setNotesHasMore(!!np.has_more); setNotesCursor(np.next_before || null);
      // Commercial rollup is cheap and drives an Overview card, load it up front.
      authFetch('/api/clients/' + clientId + '/commercial').then(setCommercial).catch(() => setCommercial(null));
      authFetch('/api/clients/' + clientId + '/intelligence').then(setIntel).catch(() => setIntel(null));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [authFetch, clientId]);
  useEffect(() => { load(); }, [load]);

  // C1-NEWS: which contacts are on the Insights email list.
  const [insights, setInsights] = useState({});
  const [insBusy, setInsBusy] = useState(false);
  const contactEmails = ((client && client.contacts) || []).map(c => String(c.email || '').trim().toLowerCase()).filter(Boolean).join(',');
  const loadInsights = useCallback(() => {
    if (!contactEmails) { setInsights({}); return Promise.resolve(); }
    return authFetch('/api/newsletter/lookup', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ emails: contactEmails.split(',') }) })
      .then(r => setInsights(r.statuses || {})).catch(() => {});
  }, [authFetch, contactEmails]);
  useEffect(() => { loadInsights(); }, [loadInsights]);
  async function addToInsights(ids) {
    setInsBusy(true);
    try {
      const r = await authFetch('/api/newsletter/from-contacts', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ contact_ids: ids }) });
      const left = (r.left_unsubscribed || 0) + (r.left_bounced || 0) + (r.left_complained || 0);
      flash(r.added ? r.added + ' added to Insights' + (left ? ', ' + left + ' left out because they left before' : '') : left ? 'Not added: they left Insights before. Put them back from the list if they asked.' : 'Already on Insights');
      await loadInsights();
    } catch (e) { flash(e.message); }
    setInsBusy(false);
  }

  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    try { const r = await authFetch('/api/clients/' + clientId + '/activity?limit=200'); setActivity(r.events || []); }
    catch (e) { setActivity([]); }
    setActivityLoading(false);
  }, [authFetch, clientId]);

  const loadShares = useCallback(async () => {
    setSharesLoading(true);
    try { const r = await authFetch('/api/clients/' + clientId + '/shares'); setShares(Array.isArray(r) ? r : (r.shares || [])); }
    catch (e) { setShares([]); }
    setSharesLoading(false);
  }, [authFetch, clientId]);

  // Lazy-load per tab, once.
  useEffect(() => {
    if (tab === 'Activity' && activity === null && !activityLoading) loadActivity();
    if (tab === 'Shares' && shares === null && !sharesLoading) loadShares();
  }, [tab, activity, activityLoading, loadActivity, shares, sharesLoading, loadShares]);

  const openShare = useCallback(async (id) => {
    setOpenShareId(id); setShareDetail(null); setShareDetailLoading(true);
    try { setShareDetail(await authFetch('/api/shares/' + id)); }
    catch (e) { setShareDetail({ error: e.message }); }
    setShareDetailLoading(false);
  }, [authFetch]);

  async function revokeShare(id) {
    const confirmed = await gtConfirm({
      title: 'Revoke this share',
      message: 'The public link stops working immediately. Anyone holding it will see a dead page.',
      confirmLabel: 'Revoke',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await authFetch('/api/shares/' + id + '/revoke', { method: 'POST' });
      flash('Share revoked');
      await loadShares();
      if (openShareId === id) openShare(id);
    } catch (e) { flash(e.message); }
  }

  function flash(msg) { setToast(msg); setTimeout(() => setToast(null), 2600); }

  // C2-ANON: release one person's named profile on an anonymous link.
  async function releaseName(shareId, it) {
    const ok = await gtConfirm({
      title: 'Release ' + (it.full_name || 'this person') + '\u2019s name',
      message: 'The client will see their full named profile on this link straight away, as on a standard share. Do this once terms are agreed and they have said yes to being introduced. It cannot be taken back.',
      confirmLabel: 'Release name',
    });
    if (!ok) return;
    try {
      await authFetch('/api/shares/' + shareId + '/items/' + it.candidate_id + '/release', { method: 'POST' });
      flash('Name released to the client');
      openShare(shareId);
    } catch (e) { flash(e.message); }
  }

  async function loadEarlierNotes() {
    if (!notesCursor || notesLoadingMore) return;
    setNotesLoadingMore(true);
    try {
      const np = await authFetch('/api/clients/' + clientId + '/notes?limit=20&before=' + encodeURIComponent(notesCursor));
      setNotes(prev => [...(np.notes || []), ...prev]); setNotesHasMore(!!np.has_more); setNotesCursor(np.next_before || null);
    } catch (e) { /* */ }
    setNotesLoadingMore(false);
  }
  async function postNote() {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const note = await authFetch('/api/clients/' + clientId + '/notes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body: newNote.trim(), author_name: userName || null }) });
      setMyClerkId(note.author_clerk_id); setNotes(prev => [...prev, note]); setNewNote('');
    } catch (e) { flash(e.message); }
    setSavingNote(false);
  }
  async function deleteNote(id) { try { await authFetch('/api/clients/' + clientId + '/notes/' + id, { method: 'DELETE' }); setNotes(prev => prev.filter(n => n.id !== id)); } catch (e) { flash(e.message); } }

  function startEdit() { setForm({ name: client.name || '', website: client.website || '', sector: client.sector || '', size: client.size || '', location: client.location || '', status: client.status || 'prospect', brief: client.brief || '' }); setEditing(true); }
  async function refreshLogo() {
    setLogoBusy(true);
    try {
      const r = await authFetch('/api/clients/' + clientId + '/logo/refresh', { method: 'POST' });
      setClient(c => ({ ...c, logo_data: r.logo_data || null }));
      flash(r.status === 'ok' ? 'Logo updated' : 'No logo found on their website, initials kept');
    } catch (e) { flash(e.message); }
    finally { setLogoBusy(false); }
  }
  async function saveEdit() { try { const u = await authFetch('/api/clients/' + clientId, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) }); setClient(c => ({ ...c, ...u })); setEditing(false); flash('Client updated'); } catch (e) { flash(e.message); } }

  async function addContact() {
    if (!cf.full_name.trim()) { flash('Contact name is required'); return; }
    try {
      const created = await authFetch('/api/clients/' + clientId + '/contacts', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cf) });
      setShowAddContact(false); setCf({ full_name: '', job_title: '', email: '', phone: '', linkedin_url: '', is_primary: false });
      await load(); flash('Contact added');
      if (created && liValid(created.linkedin_url)) lookUpContact(created.id);
    } catch (e) { flash(e.message); }
  }
  // LinkedIn lookups on a contact. The result replaces the contact in place.
  function putContact(row) { if (row && row.id) setClient(c => ({ ...c, contacts: (c.contacts || []).map(x => (x.id === row.id ? row : x)) })); }
  async function lookUpContact(id) {
    setLiBusy(b => ({ ...b, [id]: true }));
    try {
      const row = await authFetch('/api/client-contacts/' + id + '/enrich', { method: 'POST' });
      putContact(row);
      const said = { done: 'Checked on LinkedIn', needs_review: 'Not sure that LinkedIn profile is them. Check the contact.', not_found: 'That LinkedIn profile could not be read' };
      flash(said[row.enrich_status] || 'LinkedIn lookup finished');
    } catch (e) { flash(e.message); await load(); }
    finally { setLiBusy(b => { const n = { ...b }; delete n[id]; return n; }); }
  }
  async function confirmContact(id) {
    try { putContact(await authFetch('/api/client-contacts/' + id + '/enrich/confirm', { method: 'POST' })); flash('Applied from LinkedIn'); } catch (e) { flash(e.message); }
  }
  async function decideContact(id, action) {
    try { putContact(await authFetch('/api/client-contacts/' + id + '/enrich/suggestion', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action }) })); } catch (e) { flash(e.message); }
  }
  function startEditContact(ct) { setEcf({ full_name: ct.full_name || '', job_title: ct.job_title || '', email: ct.email || '', phone: ct.phone || '', linkedin_url: ct.linkedin_url || '' }); setEditContactId(ct.id); }
  async function saveContact(id) {
    if (!ecf.full_name.trim()) { flash('Contact name is required'); return; }
    const before = (client.contacts || []).find(x => x.id === id) || {};
    try {
      const row = await authFetch('/api/client-contacts/' + id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ecf) });
      setEditContactId(null); await load(); flash('Contact updated');
      if (row && liValid(row.linkedin_url) && liKey(row.linkedin_url) !== liKey(before.linkedin_url)) lookUpContact(id);
    } catch (e) { flash(e.message); }
  }
  async function deleteContact(id) { try { await authFetch('/api/client-contacts/' + id, { method: 'DELETE' }); await load(); flash('Contact removed'); } catch (e) { flash(e.message); } }
  async function makePrimary(id) { try { await authFetch('/api/client-contacts/' + id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ is_primary: true }) }); await load(); } catch (e) { flash(e.message); } }

  if (openRoleId) return <GtRoleRecord roleId={openRoleId} getToken={getToken} userName={userName} userImage={userImage} backLabel={(client && client.name) || 'Client'}
    initialCandidateId={initialCandidateId} onOpenCandidate={onOpenCandidate} onCloseCandidate={onCloseCandidate}
    onBack={() => (onCloseRole ? onCloseRole() : null)} />;

  if (loading) return <div className="gtr2"><GtLoader /></div>;
  if (error) return <div className="gtr2"><div className="gtr2-top"><button type="button" className="gtr2-back" onClick={onBack}>← Clients</button></div><div className="gtc-error">{error}</div></div>;
  if (!client) return null;

  const nm = client.name || 'Unnamed client';
  const contacts = client.contacts || [];
  const roles = client.roles || [];
  const openRoles = roles.filter(r => r.status === 'open');
  const webUrl = client.website ? (client.website.startsWith('http') ? client.website : 'https://' + client.website) : null;

  const shareCount = Array.isArray(shares) ? shares.length : (client.share_count || 0);
  const activeShares = Array.isArray(shares) ? shares.filter(s => s.status === 'active').length : null;
  const totalOpens = Array.isArray(shares) ? shares.reduce((a, s) => a + (s.opens || 0), 0) : null;

  const filteredActivity = (activity || []).filter(e => actFilter === 'all' ? true : e.group === actFilter);
  const activityKindsPresent = Array.from(new Set((activity || []).map(e => e.group)));

  async function generateBlurb() {
    setBlurbBusy(true);
    try {
      const updated = await authFetch('/api/clients/' + clientId + '/blurb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      setClient(c => ({ ...c, ...updated }));
      flash('Description written');
    } catch (e) {
      // A 422 means the model kept naming the client and nothing was saved.
      // That is the safeguard working, so it is reported as such.
      flash(e.status === 422 ? 'The description kept identifying the client, so nothing was saved. Try again or write it yourself.' : e.message);
    }
    finally { setBlurbBusy(false); }
  }

  async function saveBlurb() {
    setBlurbBusy(true);
    try {
      const updated = await authFetch('/api/clients/' + clientId + '/blurb', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ public_blurb: blurbDraft }),
      });
      setClient(c => ({ ...c, ...updated }));
      setBlurbEditing(false);
      flash('Description saved');
    } catch (e) { flash(e.message); }
    finally { setBlurbBusy(false); }
  }

  function activityLine(e) {
    const k = ACT_KIND[e.group] || ACT_KIND.audit;
    const under = [e.actor_display, e.detail_line].filter(Boolean).join(' \u00B7 ');
    return (
      <li key={e.id} className={'gta-row' + (e.group === 'win' ? ' gta-row--strong' : '')}>
        <span className={'gta-ic ' + k.cls} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={k.icon} /></svg>
        </span>
        <div className="gta-body">
          <div className="gta-head">
            <span className="gta-title">{e.title}</span>
            {e.subject ? <span className="gta-subject">{e.subject}</span> : null}
          </div>
          {under ? <div className="gta-detail"><span className="gta-dim">{under}</span></div> : null}
        </div>
        <time className="gta-time" title={ukDateTime(e.at)}>{relTime(e.at)}</time>
      </li>
    );
  }

  const primary = contacts.find(ct => ct.is_primary) || null;
  const sizeLabel = client.size ? client.size + (/^[\d\s,+–-]+$/.test(String(client.size)) ? ' people' : '') : null;
  const tabCount = { Roles: roles.length, Contacts: contacts.length, Notes: notes.length, Shares: shareCount };
  const signals = (intel && intel.signals) || [];

  return (
    <>
      <div className="gtr2">
        <div className="gtr2-top">
          <button type="button" className="gtr2-back" onClick={onBack}><Ico d={ICONS.back} />Clients</button>
          <span className="gtr2-sep">/</span>
          <span className="gtr2-crumb">{nm}</span>
          {client.ref && <span className="gtr2-ref">{client.ref}</span>}
        </div>

        {toast && <div className="gtc-toast">{toast}</div>}

        <section className="gtr2-hero">
          <div className="gtr2-cover gtr2-cover--client" />
          <div className="gtr2-hero-row">
            {client.logo_data
              ? <span className="gtr2-avatar gtr2-avatar--logo"><img src={client.logo_data} alt={nm + ' logo'} /></span>
              : <span className="gtr2-avatar" style={{ background: avatarColour(nm) }}>{initials(nm)}</span>}
            <div className="gtr2-id">
              <div className="gtr2-namerow">
                <h1 className="gtr2-name">{nm}</h1>
                <span className={'gtt-pill gtr2-status ' + (CLIENT_STATUS_PILL[client.status] || 'gtt-p-slate')}>{client.status}</span>
              </div>
              <p className="gtr2-headline">
                {primary ? <>Main contact <b className="gtr2-at-b">{primary.full_name}</b>{primary.job_title ? ', ' + primary.job_title : ''}</> : 'No main contact yet'}
              </p>
              <div className="gtr2-chips">
                {webUrl && <a className="gtr2-chip gtr2-chip--link" href={webUrl} target="_blank" rel="noreferrer"><Ico d={ICONS.globe} />{String(client.website).replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</a>}
                {client.sector && <span className="gtr2-chip gtr2-chip--domain"><Ico d={ICONS.spark} />{client.sector}</span>}
                {client.location && <span className="gtr2-chip"><Ico d={ICONS.pin} />{client.location}</span>}
                {sizeLabel && <span className="gtr2-chip"><Ico d={ICONS.users} />{sizeLabel}</span>}
                <span className="gtr2-chip"><Ico d={ICONS.user} />{client.owner_name ? 'Owned by ' + client.owner_name : 'No owner'}</span>
              </div>
            </div>
            <div className="gtr2-acts">
              <button type="button" className="gtr2-btn gtr2-btn--primary" onClick={() => setShowShareCreate(true)}><Ico d={ICONS.share} />Share profiles</button>
              <button type="button" className="gtr2-btn" onClick={() => setShowAddContact(true)}><Ico d={ICONS.plus} />Add contact</button>
              <button type="button" className="gtr2-btn" onClick={startEdit} disabled={editing}><Ico d={ICONS.edit} />Edit details</button>
            </div>
          </div>

          <div className="gtr2-stats">
            <Stat label="Open roles" value={openRoles.length} sub={roles.length + ' in total'} onClick={() => setTab('Roles')} />
            <Stat label="In flight" value={commercial ? commercial.in_flight : '—'} sub={commercial ? commercial.total_submissions + ' submitted' : null} />
            <Stat label="Placements" value={commercial ? commercial.placements : '—'} />
            <Stat label="Fees placed" value={commercial ? money(commercial.fees_placed_gbp) : '—'} />
            <Stat label="Shares" value={shareCount || 0} sub={activeShares != null ? activeShares + ' active, ' + totalOpens + ' opens' : null} onClick={() => setTab('Shares')} />
            <Stat label="Client since" value={ukDate(client.created_at)} sub={'Updated ' + ukDate(client.updated_at)} />
          </div>
        </section>

        <div className="gtr2-body">
          <div className="gtr2-main">
            <div className="gtr-card gtr2-tabcard">
              <div className="gtr2-tabs" role="tablist">
                {TABS.map(t => (
                  <button type="button" role="tab" aria-selected={tab === t} key={t} className={'gtr2-tab' + (tab === t ? ' gtr2-tab--on' : '')} onClick={() => setTab(t)}>
                    {t}{tabCount[t] ? <span className="gtr2-count">{tabCount[t]}</span> : null}
                  </button>
                ))}
              </div>

              <div className="gtr2-tabbody">
                {tab === 'Activity' && (
                  <div className="gtr2-pad">
                    <div className="gta-filterbar">
                      <button className={'gta-chip' + (actFilter === 'all' ? ' gta-chip--on' : '')} onClick={() => setActFilter('all')}>All</button>
                      {activityKindsPresent.map(k => {
                        const info = ACT_KIND[k] || ACT_KIND.audit;
                        return <button key={k} className={'gta-chip ' + info.cls + (actFilter === k ? ' gta-chip--on' : '')} onClick={() => setActFilter(k)}><span className={'gta-chipdot ' + info.cls} />{info.label}</button>;
                      })}
                      <button className="gta-refresh" onClick={loadActivity} title="Refresh">Refresh</button>
                    </div>
                    {activityLoading && activity === null ? <GtLoader compact /> : (
                      filteredActivity.length === 0 ? (
                        <div className="gtr2-empty">{activity && activity.length ? 'No activity of this type.' : 'No activity yet. Roles, submissions, shares, opens and notes all appear here as they happen. This is the client’s source of truth.'}</div>
                      ) : (
                        <ol className="gta-feed">{filteredActivity.map(activityLine)}</ol>
                      )
                    )}
                  </div>
                )}

                {tab === 'Overview' && (
                  <div className="gtr2-record">
                    <section className="gtr2-panel gtr2-panel--wide">
                      <div className="gtr2-panelh">
                        <h3>Public description</h3>
                        <span className="gtr2-panelacts">
                          {!blurbEditing && (
                            <button className="gtr2-btn gtr2-btn--sm" disabled={blurbBusy} onClick={generateBlurb}>
                              <Ico d={ICONS.spark} />{blurbBusy ? 'Writing' : (client.public_blurb ? 'Rewrite' : 'Write with AI')}
                            </button>
                          )}
                          {client.public_blurb && !blurbEditing && (
                            <button className="gtr2-btn gtr2-btn--sm" onClick={() => { setBlurbDraft(client.public_blurb || ''); setBlurbEditing(true); }}>Edit</button>
                          )}
                          {blurbEditing && (
                            <>
                              <button className="gtr2-btn gtr2-btn--sm" onClick={() => setBlurbEditing(false)}>Cancel</button>
                              <button className="gtr2-btn gtr2-btn--sm gtr2-btn--primary" disabled={blurbBusy} onClick={saveBlurb}>Save</button>
                            </>
                          )}
                        </span>
                      </div>
                      {blurbEditing ? (
                        <textarea className="gtb-edit" rows={5} value={blurbDraft} aria-label="Public description"
                          onChange={e => setBlurbDraft(e.target.value)}
                          placeholder="How this client should be described on a public advert, without naming them." />
                      ) : client.public_blurb ? (
                        <>
                          <p className="gtr2-prose">{client.public_blurb}</p>
                          <div className="gtr2-foot">
                            {client.public_blurb_model ? 'Written by ' + client.public_blurb_model : 'Written by hand'}
                            {client.public_blurb_at ? ' · ' + ukDate(client.public_blurb_at) : ''}
                          </div>
                        </>
                      ) : (
                        <div className="gtr2-empty">
                          No public description yet. This opens every advert for this client, written from their sector and domain without naming them.
                          {!client.website && !client.sector ? ' Add a website or sector first.' : ''}
                        </div>
                      )}
                    </section>

                    <section className="gtr2-panel">
                      <div className="gtr2-panelh"><h3>Roles</h3><span className="gtr2-count">{openRoles.length} open</span></div>
                      {roles.length === 0 ? <div className="gtr2-empty">No roles yet.</div> : (
                        <div className="gtr2-list">
                          {roles.slice(0, 5).map(r => (
                            <button key={r.id} type="button" className="gtr2-listrow" onClick={() => setOpenRoleId(r.ref || r.id)}>
                              <span className="gtr2-listico"><Ico d={ICONS.brief} /></span>
                              <span className="gtr2-listmain"><b>{r.title}</b><span>{[r.ref, r.location].filter(Boolean).join(' · ') || '—'}</span></span>
                              <span className={'gtt-pill ' + (ROLE_STATUS_PILL[r.status] || 'gtt-p-slate')}>{r.status}</span>
                            </button>
                          ))}
                          {roles.length > 5 && <button type="button" className="gtr2-more" onClick={() => setTab('Roles')}>View all {roles.length} roles</button>}
                        </div>
                      )}
                    </section>

                    <section className="gtr2-panel">
                      <div className="gtr2-panelh"><h3>Intelligence</h3><span className="gtr2-count">{intel && intel.sweep_meta && intel.sweep_meta.last_swept_at ? signals.length + ' signals' : 'Not swept'}</span></div>
                      {!signals.length ? (
                        <>
                          <div className="gtr2-empty">Job adverts, funding, expansion and leadership changes at {nm} appear here once a sweep runs. They tell you when to reach out and what they will need.</div>
                          <button type="button" className="gtr2-btn gtr2-btn--sm" onClick={() => setTab('Intelligence')}><Ico d={ICONS.bolt} />Run a sweep</button>
                        </>
                      ) : (
                        <div className="gtr2-list">
                          {signals.slice(0, 4).map(sg => (
                            <div key={sg.id} className="gti-cardrow">
                              <span className={'gti-score gti-t-' + ({ job_advert: 'job', funding: 'fund', leadership_change: 'lead', expansion: 'exp', hiring: 'hire', tech_change: 'tech' }[sg.signal_type] || 'news')}>{sg.relevance_score}</span>
                              <span className="gti-cardsum">{(sg.signal_data && sg.signal_data.summary) || ''}</span>
                            </div>
                          ))}
                          <button type="button" className="gtr2-more" onClick={() => setTab('Intelligence')}>View all {signals.length} signals</button>
                        </div>
                      )}
                    </section>
                  </div>
                )}

                {tab === 'Intelligence' && (
                  <div className="gtr2-pad">
                    <GtIntelligence clientId={clientId} clientName={nm} authFetch={authFetch} onSwept={load} />
                  </div>
                )}

                {tab === 'Roles' && (
                  <div className="gtr2-pad">
                    {roles.length === 0 ? <div className="gtr2-empty">No roles yet for this client. Roles created against {nm} appear here.</div> : (
                      <div className="gtr2-list">
                        {roles.map(r => (
                          <button key={r.id} type="button" className="gtr2-listrow" onClick={() => setOpenRoleId(r.ref || r.id)}>
                            <span className="gtr2-listico"><Ico d={ICONS.brief} /></span>
                            <span className="gtr2-listmain"><b>{r.title}</b><span>{[r.ref, r.stage, r.location].filter(Boolean).join(' · ') || '—'}</span></span>
                            <span className={'gtt-pill ' + (ROLE_STATUS_PILL[r.status] || 'gtt-p-slate')}>{r.status}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {tab === 'Shares' && (
                  <div className="gtr2-pad">
                    <div className="gtr2-tabtools">
                      <span>Profile bundles sent to {nm}. Every open and CV view is tracked.</span>
                      <span className="gtr2-panelacts">
                        <button className="gtr2-btn gtr2-btn--sm" onClick={loadShares} disabled={sharesLoading}>{sharesLoading ? 'Loading…' : 'Refresh'}</button>
                        <button className="gtr2-btn gtr2-btn--sm gtr2-btn--primary" onClick={() => setShowShareCreate(true)}><Ico d={ICONS.share} />Share profiles</button>
                      </span>
                    </div>
                    {sharesLoading && shares === null ? <GtLoader compact /> : (
                      (shares && shares.length) ? (
                        <div className="gtr-sharelist">
                          {shares.map(s => (
                            <div key={s.id} className="gtr-sharerow">
                              <button type="button" className="gtr-sharebody gtr2-unbutton" onClick={() => openShare(s.id)}>
                                <div className="gtr-sharetop">
                                  <span className="gtr-refbadge gtr-refbadge-sm">{s.ref}</span>
                                  <span className="gtr-sharetitle">{s.title || 'Untitled bundle'}</span>
                                  {s.mode === 'anonymous' && <span className="gtac-badge">Anonymous</span>}
                                  <span className={'gtt-pill ' + (SHARE_STATUS_PILL[s.status] || 'gtt-p-slate')}>{s.status}</span>
                                </div>
                                <div className="gtr-sharesub">
                                  To {s.contact_name || 'a contact'} · {s.candidate_count} candidate{s.candidate_count === 1 ? '' : 's'} · {s.opens ? s.opens + ' open' + (s.opens === 1 ? '' : 's') : 'not opened yet'} · sent {ukDate(s.created_at)}{s.expires_at ? ' · expires ' + ukDate(s.expires_at) : ''}
                                </div>
                              </button>
                              <div className="gtr-shareacts">
                                <button className="gtr2-btn gtr2-btn--sm" onClick={() => openShare(s.id)}>Telemetry</button>
                                {s.status === 'active' ? <button className="gtr2-btn gtr2-btn--sm gtr2-btn--danger" onClick={() => revokeShare(s.id)}>Revoke</button> : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : <div className="gtr2-empty">No profiles shared yet. When you send a candidate bundle to a contact, every open and CV view is tracked here.</div>
                    )}
                  </div>
                )}

                {tab === 'Contacts' && (
                  <div className="gtr2-pad">
                    <div className="gtr2-tabtools">
                      <span>The main contact receives shared candidate profiles.</span>
                      <span className="gtr2-tabacts">{/* C1-NEWS */}
                        {contacts.some(ct => ct.email && !insights[String(ct.email).trim().toLowerCase()]) && <button className="gtr2-btn gtr2-btn--sm" disabled={insBusy} onClick={() => addToInsights(contacts.filter(ct => ct.email && !insights[String(ct.email).trim().toLowerCase()]).map(ct => ct.id))}>Add all to Insights</button>}
                        <button className="gtr2-btn gtr2-btn--sm gtr2-btn--primary" onClick={() => setShowAddContact(true)}><Ico d={ICONS.plus} />Add contact</button>
                      </span>
                    </div>
                    {contacts.length === 0 ? <div className="gtr2-empty">No contacts yet. Add the person who will receive shared candidate profiles.</div> : (
                      <div className="gtr-contactlist">
                        {contacts.map(ct => (
                          <div key={ct.id} className="gtr-contactrow">
                            <span className="gtr-contactav" style={{ background: avatarColour(ct.full_name) }}>{initials(ct.full_name)}</span>
                            {editContactId === ct.id ? (
                              <>
                                <div className="gtr-contactbody gtr-contactedit">
                                  <input aria-label="Full name" value={ecf.full_name} onChange={e => setEcf({ ...ecf, full_name: e.target.value })} placeholder="Full name" />
                                  <input aria-label="Job title" value={ecf.job_title} onChange={e => setEcf({ ...ecf, job_title: e.target.value })} placeholder="Job title" />
                                  <input aria-label="Email" value={ecf.email} onChange={e => setEcf({ ...ecf, email: e.target.value })} placeholder="Email" />
                                  <input aria-label="Phone" value={ecf.phone} onChange={e => setEcf({ ...ecf, phone: e.target.value })} placeholder="Phone" />
                                  <input aria-label="LinkedIn profile" value={ecf.linkedin_url} onChange={e => setEcf({ ...ecf, linkedin_url: e.target.value })} placeholder="linkedin.com/in/..." />
                                </div>
                                <div className="gtr-contactacts">
                                  <button className="gtr2-btn gtr2-btn--sm" onClick={() => setEditContactId(null)}>Cancel</button>
                                  <button className="gtr2-btn gtr2-btn--sm gtr2-btn--primary" onClick={() => saveContact(ct.id)}>Save</button>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="gtr-contactbody">
                                  <div className="gtr-contactname">{ct.full_name}{ct.is_primary && <span className="gtr-primarybadge">Main contact</span>}
                                    {liValid(ct.linkedin_url) && <a className="gtce-lilink" href={ct.linkedin_url} target="_blank" rel="noreferrer">LinkedIn</a>}
                                    {ct.email && insights[String(ct.email).trim().toLowerCase()] && (() => { const st = insights[String(ct.email).trim().toLowerCase()]; return <span className={'gtr-insbadge gtr-insbadge--' + (st === 'subscribed' ? 'on' : st === 'pending' ? 'wait' : 'off')} title="Insights email list">{st === 'subscribed' ? 'On Insights' : st === 'pending' ? 'Insights: not confirmed' : 'Left Insights'}</span>; })()}</div>
                                  <div className="gtr-contactsub">{[ct.job_title, ct.email, ct.phone].filter(Boolean).join(' · ') || '—'}</div>
                                  {(ct.headline || ct.location) && <div className="gtr-contactsub gtce-head">{[ct.headline, ct.location].filter(Boolean).join(' · ')}</div>}
                                  <ContactLinkedIn ct={ct} busy={!!liBusy[ct.id]} onConfirm={() => confirmContact(ct.id)} onDecide={a => decideContact(ct.id, a)} />
                                </div>
                                <div className="gtr-contactacts">
                                  {liValid(ct.linkedin_url) && <button className="gtr2-btn gtr2-btn--sm" disabled={!!liBusy[ct.id]} onClick={() => lookUpContact(ct.id)}>{liBusy[ct.id] ? 'Looking up' : (ct.enriched_at ? 'Refresh from LinkedIn' : 'Look up on LinkedIn')}</button>}
                                  {ct.email && !insights[String(ct.email).trim().toLowerCase()] && <button className="gtr2-btn gtr2-btn--sm" disabled={insBusy} onClick={() => addToInsights([ct.id])}>Add to Insights</button>}
                                  <button className="gtr2-btn gtr2-btn--sm" onClick={() => startEditContact(ct)}>Edit</button>
                                  {!ct.is_primary && <button className="gtr2-btn gtr2-btn--sm" onClick={() => makePrimary(ct.id)}>Make main contact</button>}
                                  <button className="gtr2-btn gtr2-btn--sm gtr2-btn--danger" onClick={() => deleteContact(ct.id)}>Remove</button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {tab === 'Notes' && (
                  <div className="gtr-notes">
                    <div className="gtr-notelist">
                      {notesHasMore && <button className="gtr-showmore" onClick={loadEarlierNotes} disabled={notesLoadingMore}>{notesLoadingMore ? 'Loading…' : 'Show earlier'}</button>}
                      {notes.length === 0 && <div className="gtr2-empty">No notes yet. Add the first one below.</div>}
                      {notes.map(note => {
                        const isMe = myClerkId ? note.author_clerk_id === myClerkId : false;
                        const ac = avatarColour(note.author_name || note.author_clerk_id);
                        return (
                          <div className={'gtr-noterow' + (isMe ? ' gtr-note-me' : '')} key={note.id}>
                            {isMe && userImage ? <img className="gtr-noteav gtr-noteav-img" src={userImage} alt="" /> : <span className="gtr-noteav" style={{ background: isMe ? '#1FA9B2' : ac }}>{initials(note.author_name || 'T')}</span>}
                            <div className="gtr-notebody">
                              <div className="gtr-notemeta"><span>{note.author_name || 'Team'}</span><span className="gtr-notetime">{note.created_at ? new Date(note.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span></div>
                              <div className={'gtr-notebubble' + (isMe ? ' gtr-notebubble-me' : '')}>{note.body}<button className="gtr-notedel" title="Delete note" aria-label="Delete note" onClick={() => deleteNote(note.id)}>×</button></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="gtr-notecompose">
                      <textarea value={newNote} aria-label="New note" onChange={e => setNewNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postNote(); } }} placeholder="Add a note. Enter to send, Shift+Enter for a new line." rows={2} />
                      <button className="gtc-primary" onClick={postNote} disabled={savingNote || !newNote.trim()}>{savingNote ? '…' : 'Send'}</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <aside className="gtr2-side">
            <div className="gtr-card">
              <div className="gtr-cardh">
                Client information
                {!editing ? <button type="button" className="gtr-cardh-edit" onClick={startEdit}>Edit</button>
                  : <span className="gtr2-panelacts"><button type="button" className="gtr-cardh-edit gtr2-muted" onClick={() => setEditing(false)}>Cancel</button><button type="button" className="gtr-cardh-edit" onClick={saveEdit}>Save</button></span>}
              </div>
              {!editing ? (
                <div className="gtr-props">
                  <Prop k="Website" v={webUrl ? <a className="gtt-link" href={webUrl} target="_blank" rel="noreferrer">{String(client.website).replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</a> : '—'} />
                  <Prop k="Sector" v={client.sector || '—'} />
                  <Prop k="Size" v={client.size || '—'} />
                  <Prop k="Location" v={client.location || '—'} />
                  <Prop k="Status" v={<span style={{ textTransform: 'capitalize' }}>{client.status}</span>} />
                  <Prop k="Owner" v={client.owner_name || '—'} />
                  <Prop k="Added" v={ukDate(client.created_at)} />
                  <Prop k="Last updated" v={ukDate(client.updated_at)} />
                  <Prop k="Logo" v={client.website
                    ? <button type="button" className="gtr-cardh-edit" onClick={refreshLogo} disabled={logoBusy}>{logoBusy ? 'Fetching' : (client.logo_data ? 'Refresh from website' : 'Fetch from website')}</button>
                    : 'Add a website first'} />
                </div>
              ) : (
                <div className="gtr-editform">
                  <label className="gtc-field"><span>Name</span><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
                  <label className="gtc-field"><span>Website</span><input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} /></label>
                  <label className="gtc-field"><span>Sector</span><SectorField value={form.sector} onChange={v => setForm({ ...form, sector: v })} authFetch={authFetch} /></label>
                  <label className="gtc-field"><span>Size</span><input value={form.size} onChange={e => setForm({ ...form, size: e.target.value })} /></label>
                  <label className="gtc-field"><span>Location</span><input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} /></label>
                  <label className="gtc-field"><span>Status</span><GtSelect value={form.status} options={STATUS_OPTS} onChange={v => setForm({ ...form, status: v })} /></label>
                  <label className="gtc-field"><span>Brief</span><textarea rows={4} value={form.brief} onChange={e => setForm({ ...form, brief: e.target.value })} /></label>
                  <div className="gtr-editactions">
                    <button className="gtc-primary" onClick={saveEdit}>Save changes</button>
                    <button className="gtc-colbtn" onClick={() => setEditing(false)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>

            <GtClientFacts key={client.id + ':' + (client.website || '') + ':' + ((intel && intel.sweep_meta && intel.sweep_meta.last_swept_at) || '')} clientId={client.id} hasWebsite={!!client.website} authFetch={authFetch} />{/* C1-CLIENTENRICH */}

            <div className="gtr-card">
              <div className="gtr-cardh">Commercial</div>
              {commercial ? (
                <div className="gtr-props">
                  <Prop k="Placements" v={commercial.placements} />
                  <Prop k="In flight" v={commercial.in_flight} />
                  <Prop k="Fees placed" v={money(commercial.fees_placed_gbp)} />
                  <Prop k="Rate card" v={commercial.rate_card_min || commercial.rate_card_max ? money(commercial.rate_card_min) + '–' + money(commercial.rate_card_max) : '—'} />
                  <Prop k="Submissions" v={commercial.total_submissions} />
                  {commercial.placements > 0 && commercial.fees_placed_gbp === 0 ? <div className="gtr2-hint">Fee amounts land once captured at placement time.</div> : null}
                </div>
              ) : <div className="gtr2-empty">No commercial activity yet.</div>}
            </div>

            <div className="gtr-card">
              <div className="gtr-cardh">Contacts <button type="button" className="gtr-cardh-edit" onClick={() => setShowAddContact(true)}>Add</button></div>
              {contacts.length === 0 ? <div className="gtr2-empty">No contacts yet.</div> : (
                <div className="gtr2-list">
                  {contacts.slice(0, 5).map(ct => (
                    <button key={ct.id} type="button" className="gtr2-listrow" onClick={() => setTab('Contacts')}>
                      <span className="gtr2-miniav" style={{ background: avatarColour(ct.full_name) }}>{initials(ct.full_name)}</span>
                      <span className="gtr2-listmain"><b>{ct.full_name}</b><span>{ct.job_title || ct.email || '—'}</span></span>
                      {ct.is_primary && <span className="gtr-primarybadge">Main</span>}
                    </button>
                  ))}
                  {contacts.length > 5 && <button type="button" className="gtr2-more" onClick={() => setTab('Contacts')}>View all {contacts.length} contacts</button>}
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {showShareCreate && (
        <GtShareCreate clientId={clientId} client={client} roleId={null} getToken={getToken}
          onClose={() => setShowShareCreate(false)}
          onCreated={() => { loadShares(); if (tab !== 'Shares') setTab('Shares'); }} />
      )}

      {openShareId && (
        <div className="gtc-modal-backdrop" onClick={() => setOpenShareId(null)}>
          <div className="gtc-modal gtc-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="gtc-modal-head">Share telemetry</div>
            <div className="gtc-modal-body">
              {shareDetailLoading ? <GtLoader compact /> : (
                (shareDetail && shareDetail.error) ? <div className="gtc-error">{shareDetail.error}</div> : (
                  shareDetail ? (
                    <div className="gtr-sharedetail">
                      <div className="gtr-sd-top">
                        <span className="gtr-refbadge gtr-refbadge-sm">{shareDetail.ref}</span>
                        <span className="gtr-sharetitle">{shareDetail.title || 'Untitled bundle'}</span>
                        {shareDetail.mode === 'anonymous' && <span className="gtac-badge">Anonymous</span>}
                        <span className={'gtt-pill ' + (SHARE_STATUS_PILL[shareDetail.status] || 'gtt-p-slate')}>{shareDetail.status}</span>
                      </div>
                      <div className="gtr-sd-meta">
                        To {shareDetail.contact_name || 'a contact'} · sent {ukDate(shareDetail.created_at)}
                        {shareDetail.expires_at ? ' · expires ' + ukDate(shareDetail.expires_at) : ''}
                        {shareDetail.view_count ? ' · ' + shareDetail.view_count + ' total views' : ''}
                        {shareDetail.first_viewed_at ? ' · first opened ' + ukDateTime(shareDetail.first_viewed_at) : ''}
                      </div>

                      {shareDetail.token && shareDetail.status === 'active' && (
                        <>
                          <div className="gtr-sd-sec">Public link</div>
                          <div className="gtsh-linkbox">
                            <input readOnly value={window.location.origin + '/s/' + shareDetail.token} onFocus={e => e.target.select()} />
                            <button className="gtc-primary" onClick={() => { navigator.clipboard.writeText(window.location.origin + '/s/' + shareDetail.token); flash('Link copied'); }}>Copy</button>
                          </div>
                        </>
                      )}

                      <div className="gtr-sd-sec">Candidates in this bundle</div>
                      <div className="gtr-sd-cands">
                        {(shareDetail.items || []).map(it => (
                          <div key={it.id || it.candidate_id} className="gtr-sd-cand">
                            <span className="gtr-sd-candname">{it.full_name || it.real_name || ('Candidate ' + (it.ref || it.candidate_id))}</span>
                            {shareDetail.mode === 'anonymous' && (() => {
                              // C2-ANON: interest shows here; the name goes to the client only when you release it.
                              const v = (shareDetail.events || []).find((ev) => ev.candidate_id === it.candidate_id && (ev.event === 'client_interested' || ev.event === 'client_declined'));
                              return (
                                <span className="gtac-sdstate">
                                  {v && <span className={'gtac-st ' + (v.event === 'client_interested' ? 'gtac-st--ok' : 'gtac-st--warn')}>{v.event === 'client_interested' ? 'Interested' : 'Declined'}</span>}
                                  {it.released_at
                                    ? <span className="gtac-st gtac-st--rel">Name released {ukDate(it.released_at)}</span>
                                    : (shareDetail.status === 'active' && <button type="button" className="gtr2-btn gtr2-btn--sm" onClick={() => releaseName(shareDetail.id, it)}>Release name</button>)}
                                </span>
                              );
                            })()}
                            <span className="gtr-sd-candopens">{it.opens ? it.opens + ' open' + (it.opens === 1 ? '' : 's') : 'not opened'}</span>
                          </div>
                        ))}
                        {(!shareDetail.items || shareDetail.items.length === 0) ? <div className="gtr-empty-sm">No candidates recorded on this bundle.</div> : null}
                      </div>

                      <div className="gtr-sd-sec">Recent events</div>
                      <ol className="gta-feed gta-feed-compact">
                        {(shareDetail.events || []).slice(0, 40).map((ev, i) => (
                          <li key={i} className="gta-row">
                            <span className="gta-ic gta-k-open" aria-hidden="true"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={ACT_KIND.share_open.icon} /></svg></span>
                            <div className="gta-body"><div className="gta-head"><span className="gta-title">{ev.event}</span>{ev.section ? <span className="gta-subject">{ev.section}</span> : null}</div></div>
                            <time className="gta-time" title={ukDateTime(ev.occurred_at)}>{relTime(ev.occurred_at)}</time>
                          </li>
                        ))}
                        {(!shareDetail.events || shareDetail.events.length === 0) ? <div className="gtr-empty-sm">No telemetry captured yet. Events appear once the contact opens the link.</div> : null}
                      </ol>
                    </div>
                  ) : null
                )
              )}
            </div>
            <div className="gtc-modal-foot">
              {shareDetail && !shareDetail.error && shareDetail.status === 'active' ? <button type="button" className="gtc-btn-ghost gtr-danger" onClick={() => revokeShare(openShareId)}>Revoke link</button> : <span />}
              <button type="button" className="gtc-primary" onClick={() => setOpenShareId(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showAddContact && (
        <div className="gtc-modal-backdrop" onClick={() => setShowAddContact(false)}>
          <div className="gtc-modal" onClick={e => e.stopPropagation()}>
            <div className="gtc-modal-head">Add contact to {nm}</div>
            <div className="gtc-modal-body">
              <label className="gtc-field"><span>Full name</span><input value={cf.full_name} onChange={e => setCf({ ...cf, full_name: e.target.value })} placeholder="Dana Ops" /></label>
              <label className="gtc-field"><span>Job title</span><input value={cf.job_title} onChange={e => setCf({ ...cf, job_title: e.target.value })} placeholder="Head of Talent" /></label>
              <label className="gtc-field"><span>Email</span><input value={cf.email} onChange={e => setCf({ ...cf, email: e.target.value })} placeholder="dana@nimbus.ai" /></label>
              <label className="gtc-field"><span>Phone</span><input value={cf.phone} onChange={e => setCf({ ...cf, phone: e.target.value })} placeholder="Optional" /></label>
              <label className="gtc-field"><span>LinkedIn profile</span><input value={cf.linkedin_url} onChange={e => setCf({ ...cf, linkedin_url: e.target.value })} placeholder="linkedin.com/in/... (we look them up)" /></label>
              <label className="gtc-checkrow"><input type="checkbox" checked={cf.is_primary} onChange={e => setCf({ ...cf, is_primary: e.target.checked })} /><span>Main contact (receives share links)</span></label>
            </div>
            <div className="gtc-modal-foot"><button type="button" className="gtc-btn-ghost" onClick={() => setShowAddContact(false)}>Cancel</button><button type="button" className="gtc-primary" onClick={addContact}>Add contact</button></div>
          </div>
        </div>
      )}
    </>
  );
}

// LinkedIn profile links in one comparable form.
function liKey(v) {
  if (!v) return null;
  let t = String(v).trim().toLowerCase().split('?')[0].split('#')[0];
  t = t.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/^[a-z]{2}\.linkedin\.com/, 'linkedin.com').replace(/\/+$/, '');
  return /^linkedin\.com\/in\/[^/]+/.test(t) ? t : null;
}
function liValid(v) { return !!liKey(v); }

// What the LinkedIn lookup found for one contact, and anything waiting on a person.
function ContactLinkedIn({ ct, busy, onConfirm, onDecide }) {
  const d = ct.linkedin_data || {};
  if (busy || ct.enrich_status === 'running') return <div className="gtce-st gtce-st--run"><span className="gten-spin" aria-hidden="true" />Looking them up on LinkedIn</div>;
  if (!ct.enrich_status) return null;
  const when = ct.enriched_at ? ukDate(ct.enriched_at) : '';
  return (
    <>
      {ct.enrich_status === 'done' && <div className="gtce-st">Checked on LinkedIn {when}</div>}
      {ct.enrich_status === 'done' && d.moved_to && <div className="gtce-st gtce-st--warn">LinkedIn shows they now work at <b>{d.moved_to}</b></div>}
      {ct.enrich_status === 'done' && d.suggestion && (
        <div className="gtce-st gtce-st--sug">LinkedIn title: <b>{d.suggestion.value}</b>
          <button type="button" className="gtce-act" onClick={() => onDecide('accept')}>Use this</button>
          <button type="button" className="gtce-act gtce-act--muted" onClick={() => onDecide('dismiss')}>Dismiss</button>
        </div>
      )}
      {ct.enrich_status === 'needs_review' && (
        <div className="gtce-st gtce-st--warn">Not sure the LinkedIn profile is them{d.reason ? ': ' + d.reason : ''}
          {d.result && <button type="button" className="gtce-act" onClick={onConfirm}>I have checked: it is them</button>}
        </div>
      )}
      {ct.enrich_status === 'not_found' && <div className="gtce-st">LinkedIn profile could not be read {when}</div>}
      {ct.enrich_status === 'failed' && <div className="gtce-st gtce-st--bad">LinkedIn lookup failed{d.error ? ': ' + d.error : ''}</div>}
    </>
  );
}

function Prop({ k, v }) { return <div className="gtr-prop"><span className="gtr-prop-k">{k}</span><span className="gtr-prop-v">{v}</span></div>; }
